import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import './Topfold.css';
import logo2 from '../assets/images/logo192.png';
import Banner from './Banner';
import Footer from './Footer';

const repositoryUrl = 'https://tlo.mit.edu/understand-ip/exploring-mit-open-source-license-comprehensive-guide';
const usageStatsUrl = 'https://event-package-website-stat.streamlit.app/';
const affiliationUrl =
  'https://www.southalabama.edu/colleges/engineering/se/';

const openUsageStatsPopup = () => {
  const width = Math.min(700, window.screen.availWidth);
  const height = Math.min(900, window.screen.availHeight);
  const left = Math.max(0, (window.screen.availWidth - width) / 2);
  const top = Math.max(0, (window.screen.availHeight - height) / 2);

  const popup = window.open(
    usageStatsUrl,
    'UsageStats',
    `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`
  );

  if (!popup) {
    window.alert(
      'Please allow popups for this site to view usage statistics.'
    );
    return;
  }

  popup.opener = null;
  popup.focus();
};

const RepositoryStar = () => {
  const [pressCount, setPressCount] = useState(0);
  const pressesRemaining = 3 - pressCount;

  const handleStarPress = () => {
    const nextCount = pressCount + 1;

    if (nextCount >= 3) {
      setPressCount(0);
      window.open(repositoryUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    setPressCount(nextCount);
  };

  return (
    <button
      className={`three-press-star ${pressCount > 0 ? 'is-active' : ''}`}
      type="button"
      onClick={handleStarPress}
      aria-label={`Press the star ${pressesRemaining} more ${
        pressesRemaining === 1 ? 'time' : 'times'
      } to open the project repository`}
      title={`Press ${pressesRemaining} more ${
        pressesRemaining === 1 ? 'time' : 'times'
      } to open the repository`}
    >
      <span className="three-press-star-icon" aria-hidden="true">&#9733;</span>
      <span className="three-press-star-copy">
        <strong>Support this project</strong>
        <small>
          {pressCount === 0
            ? 'Press the star three times'
            : `${pressesRemaining} ${
              pressesRemaining === 1 ? 'press' : 'presses'
            } remaining`}
        </small>
      </span>
      <span className="star-progress" aria-hidden="true">
        {[0, 1, 2].map((step) => (
          <span
            className={step < pressCount ? 'is-filled' : ''}
            key={step}
          />
        ))}
      </span>
    </button>
  );
};

const learningTopics = [
  {
    number: '01',
    title: 'Event delineation',
    description:
      'Identify the beginning, peak, and end of drainage events using measured flow behavior and consistent thresholds.',
  },
  {
    number: '02',
    title: 'Baseflow separation',
    description:
      'Separate event-driven discharge from the slower background flow that persists between rainfall or drainage responses.',
  },
  {
    number: '03',
    title: 'Phosphorus analysis',
    description:
      'Examine concentration, load, and flow-weighted water-quality patterns to better understand phosphorus transport.',
  },
];

const workflowSteps = [
  'Prepare time-series flow and water-quality observations.',
  'Choose an event threshold or separation method.',
  'Run the analysis and inspect calculated event metrics.',
  'Export results for interpretation, teaching, or further research.',
];

const Topfold = () => (
  <>
    <Banner />

    <main className="landing-page">
    <section className="hero-section">
      <img
        className="hero-logo-watermark"
        src={logo2}
        alt=""
        aria-hidden="true"
      />

      <div className="hero-content">
        <p className="section-eyebrow">Hydrology education and analysis</p>
        <h1>Understand drainage events from flow to water quality.</h1>
        <p className="hero-subtitle">
          A focused web toolkit for delineating hydrologic events, separating
          baseflow, and exploring phosphorus transport in drainage datasets.
        </p>

        <div className="hero-actions">
          <Link className="primary-action" to="/perform-analysis">
            Start an analysis
          </Link>
          <Link
            className="secondary-action"
            to="/event-delineating-methods"
          >
            Explore the methods
          </Link>
          <button
            className="secondary-action hero-action-button"
            type="button"
            onClick={openUsageStatsPopup}
          >
            Usage statistics
          </button>
          <a
            className="secondary-action"
            href={affiliationUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Affiliation
          </a>
        </div>

        <div className="hero-audience" aria-label="Designed for">
          <span>Researchers</span>
          <span>Students</span>
          <span>Water-quality professionals</span>
        </div>
      </div>

    </section>

    <section className="learning-section" aria-labelledby="learning-title">
      <div className="section-introduction">
        <p className="section-eyebrow">Core concepts</p>
        <h2 id="learning-title">Learn what the data is telling you.</h2>
        <p>
          Each module connects a hydrologic concept with a practical analysis
          workflow, helping users move from raw observations to interpretable
          event-level information.
        </p>
      </div>

      <div className="feature-grid">
        {learningTopics.map((topic) => (
          <article className="feature-card" key={topic.number}>
            <span className="feature-number">{topic.number}</span>
            <h3>{topic.title}</h3>
            <p>{topic.description}</p>
          </article>
        ))}
      </div>
    </section>

    <section className="value-section">
      <div className="value-copy">
        <p className="section-eyebrow">Built for learning and research</p>
        <h2>Clear enough to teach. Structured enough to investigate.</h2>
        <p>
          The application brings related analysis tools into one accessible
          environment. Students can connect equations to real data, while
          researchers can rapidly inspect events and prepare results for
          deeper statistical analysis.
        </p>

        <div className="value-points">
          <div>
            <strong>Transparent methods</strong>
            <span>Understand the reasoning behind each calculation.</span>
          </div>
          <div>
            <strong>Focused workflows</strong>
            <span>Move from input data to useful outputs without clutter.</span>
          </div>
          <div>
            <strong>Connected analysis</strong>
            <span>Relate hydrologic response to phosphorus and water quality.</span>
          </div>
        </div>
      </div>

      <div className="workflow-panel">
        <p className="section-eyebrow">Typical workflow</p>
        <ol>
          {workflowSteps.map((step, index) => (
            <li key={step}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <p>{step}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="landing-cta">
      <div>
        <p className="section-eyebrow">Ready to explore your data?</p>
        <h2>Turn drainage observations into a clearer hydrologic story.</h2>
      </div>
      <Link className="primary-action" to="/perform-analysis">
        Open the analysis workspace
      </Link>
    </section>

    <section className="open-source-section" aria-labelledby="open-source-title">
      <div className="open-source-copy">
        <p className="section-eyebrow">Open source and made with care</p>
        <h2 id="open-source-title">
          Built with <span className="love-mark" aria-label="love">&hearts;</span>
          {' '}for hydrology learners and researchers.
        </h2>
        <p>
          This project is shared under the MIT License so others can study,
          adapt, and improve the work. If the tools support your research or
          learning, starring the repository is a simple way to show support.
        </p>
      </div>

      <div className="open-source-actions">
        <a
          className="license-link"
          href={`${repositoryUrl}/blob/main/LICENSE`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Read the MIT License
        </a>
      </div>
    </section>
    </main>

    <Footer />
  </>
);

export default Topfold;